/**
 * Options this app passes to Auth0's silent token call.
 *
 * Narrower than the SDK's own `GetTokenSilentlyOptions` on purpose: these are the only fields we
 * set, and keeping the alias local lets the test doubles stay small.
 */
export type GetAccessTokenSilentlyOptions = {
  cacheMode?: "on" | "off" | "cache-only";
  authorizationParams?: { organization?: string };
};

/**
 * `getAccessTokenSilently` as this app has to treat it.
 *
 * `@auth0/auth0-spa-js` typed the no-options overload as `Promise<string>` until 2.26.0, but a
 * `cache-only` read has always resolved `undefined` on a miss — it returns early instead of
 * throwing (LAR-437). 2.26.0 widened the published type to `Promise<string | undefined>` to match.
 * Mirroring it here means every caller has to say what a miss means instead of passing a
 * non-token on.
 */
export type GetAccessTokenSilently = (
  options?: GetAccessTokenSilentlyOptions
) => Promise<string | undefined>;

/** Rejection used when a silent token call resolved without a token. */
export class MissingAccessTokenError extends Error {
  readonly error = "missing_access_token";
  constructor(operation: string) {
    super(`Silent token call (${operation}) resolved without an access token`);
    this.name = "MissingAccessTokenError";
  }
}

/**
 * Narrow a silent-call result to a token, or throw.
 *
 * Nothing downstream may receive a non-token: handing `undefined` on wiped the stored token and
 * then read as "no organization", which sent the user back through an interactive login on every
 * page load that raced the SDK's cache (LAR-437). Throwing keeps each caller on the error path it
 * already had — `jwtDecode(undefined)` reached the same `catch` — but with a legible cause.
 */
export const requireAccessToken = (
  token: string | undefined,
  operation: string
): string => {
  if (!token) throw new MissingAccessTokenError(operation);
  return token;
};
